import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useAuthToken } from "@convex-dev/auth/react";
import { LoginPage } from "@/features/auth/login-page";
import { LoadingScreen } from "@/components/loading-screen";
import { api } from "@/lib/convex-api";
import { useEffect, useRef } from "react";

export const Route = createFileRoute("/login")({
  component: LoginRoute,
});

function LoginRoute() {
  // Magic links can land on `/login` even if a valid session already exists.
  // If the user is authenticated, always route them into the app instead of
  // showing the login screen (which feels like a logout).
  const user = useQuery(api.users.me);
  const authToken = useAuthToken();
  const bridgeStartedRef = useRef(false);
  const redirect =
    typeof globalThis !== "undefined"
      ? (new URL(globalThis.location.href).searchParams.get("redirect") ??
        undefined)
      : undefined;

  useEffect(() => {
    if (!user || !redirect) return;
    let redirectUrl: URL;
    try {
      redirectUrl = new URL(redirect);
    } catch {
      globalThis.location.assign(redirect);
      return;
    }
    if (redirectUrl.origin === globalThis.location.origin) {
      globalThis.location.assign(redirect);
      return;
    }
    if (!authToken || bridgeStartedRef.current) return;
    bridgeStartedRef.current = true;
    const bridgeUrl = new URL(redirect);
    bridgeUrl.searchParams.set("mdsync_bridge", "1");
    void fetch(bridgeUrl.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then(async (response) => {
        const payload = (await response.json().catch(() => null)) as {
          redirectTo?: string;
          loginRedirect?: string;
        } | null;
        const redirectTo = payload?.redirectTo;
        if (redirectTo) {
          globalThis.location.assign(redirectTo);
          return;
        }
        const loginRedirect = payload?.loginRedirect;
        if (loginRedirect) {
          globalThis.location.assign(loginRedirect);
          return;
        }
        globalThis.location.assign(redirect);
      })
      .catch(() => {
        globalThis.location.assign(redirect);
      });
  }, [authToken, user, redirect]);

  if (user === undefined) {
    return <LoadingScreen />;
  }

  if (user) {
    if (redirect) {
      return <LoadingScreen />;
    }
    globalThis.location.assign("/app/");
    return <LoadingScreen />;
  }

  return <LoginPage redirectTo={redirect} />;
}
